/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { RootState, AppDispatch } from '~app/store';
import { setFocus } from './utils';

// ========== INITIAL STATE

export type SurveyFormatState = {
  /**
   * The currently registered views (eg. questions, page navigation, page description).
   * Used for determining the currently active view, setting the next view active, scrolling to a view, etc.
   *  */
  views: string[];
  /** The currently active view - triggers style changes and affects navigation */
  active: string | null;
  /**
   * Whether there is currently a scroll animation occurring.
   * Used in ActiveViewScrollManager to prevent multiple scroll animations from occurring at the same time, as well as preventing unnecessary calculations on scroll.
   *  */
  isAnimating: boolean;
};

export const initialState: SurveyFormatState = {
  views: [],
  active: null,
  isAnimating: false,
};

// ========== SLICE

export const surveyFormatSlice = createSlice({
  name: 'surveyFormat', // what will show up in the redux dev tools (eg. "surveyFormat/register")
  initialState,
  reducers: {
    register: (state, action: PayloadAction<string>) => {
      state.views.push(action.payload);
    },
    unregister: (state, action: PayloadAction<string>) => {
      state.views = state.views.filter(v => v !== action.payload);
    },
    setActive: (state, { payload: name }: PayloadAction<string | null>) => {
      if (name !== null) {
        state.active = state.views.find(v => v === name) ?? null;
      }
    },
    setAnimating: (state, action: PayloadAction<boolean>) => {
      state.isAnimating = action.payload;
    },
    reset: () => initialState,
  },
});

// ========== ACTIONS

export const { register, unregister, setActive, setAnimating, reset } = surveyFormatSlice.actions;

// ========== THUNKS
// The built-in createAsyncThunk() function provides access to the state and dispatch actions via thunkAPI parameter - useful for writing reducers that cause side-effects (eg. dispatching other actions) which wouldn't work correctly in a regular reducer.
// Thunks are typically used for async operations: https://redux-toolkit.js.org/api/createAsyncThunk

export const activateView = createAsyncThunk('surveyFormat/activateView', (name: string | null, thunkAPI) => {
  const dispatch = thunkAPI.dispatch as AppDispatch;

  dispatch(setActive(name));
  if (name) setFocus(name);
});

export const setNextActive = createAsyncThunk('surveyFormat/setNextActive', (_, thunkAPI): void => {
  const { surveyFormatState } = thunkAPI.getState() as RootState;
  const activeIndex = surveyFormatState.views.findIndex(v => v === surveyFormatState.active);
  const nextActive = surveyFormatState.views[(activeIndex as number) + 1] || null;
  if (nextActive) {
    setFocus(nextActive);
  }
});

// ========== SELECTORS
// These can either be specified here and exported (as below), or accessed directly within components
// eg. const active = useAppSelector(state => state.surveyFormat.active);

/** The survey format state object */
const selectSurveyFormatState = (state: RootState): SurveyFormatState => state.surveyFormatState;

/** The currently registered views (eg. questions, page navigation, page description). */
export const selectViews = createSelector(selectSurveyFormatState, surveyFormatState => surveyFormatState.views);

/** The currently active view - triggers style changes and affects navigation */
export const selectActive = createSelector(selectSurveyFormatState, surveyFormatState => surveyFormatState.active);

/** Whether there is currently a scroll animation occurring. */
export const selectIsAnimating = createSelector(
  selectSurveyFormatState,
  surveyFormatState => surveyFormatState.isAnimating
);

// ========== EXPORT

export { setFocus };

export default surveyFormatSlice.reducer;
